<template>

  <v-container fluid class="px-0 user-item">

    <!-- Breadcrumb & Actions -->
    <v-row class="mb-8">
      <v-col sm="8" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-account-multiple</v-icon>
        <router-link :to="{ name: 'user-list' }">Org Members</router-link>
        <div class="separator">/</div>
        <div class="active-item" v-if="user">{{ userTitleForDisplay }}</div>
      </v-col>
      <v-col sm="4" align="right">
        <a class="pr-4" style="font-size:16px;" @click="close">Close</a>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container v-if="isDataLoading" class="d-flex flex-column align-center justify-center">
      <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
    </v-container>

    <!-- Page Content -->
    <v-container fluid class="mt-2 page-content" v-else-if="user">

      <!-- Page Header Area -->
      <v-container fluid class="ma-0 pa-0 pb-8 page-header-area">
        <!-- Display name -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12" class="pa-0 ma-0">
            <div class="page-label">{{ user.display_name }}</div>
          </v-col>
        </v-row>
      </v-container>

      <!-- User management area -->
      <v-container fluid class="ma-0 pa-0">
        <v-row class="pa-0 ma-0">
          <!--------------->
          <!-- User info -->
          <!--------------->
          <v-col cols="12" xs="12" sm="7" md="5" lg="4" class="pa-0 ma-0 pb-10" align="left" justify="center">
            <div>
              <span class="form-field-label">Email:</span> {{ user.email }}
            </div>
            <div class="mt-3">
              <span class="form-field-label">First name:</span> {{ user.first_name }}
            </div>
            <div class="mt-3">
              <span class="form-field-label">Last name:</span> {{ user.last_name }}
            </div>
            <div class="mt-5">
              <span class="form-field-label pr-2">Active roles:</span>
              <v-chip v-for="role in userRoles"
                  label
                  class="mb-2 mr-2 white--text"
                  :color="getRoleChipColor(role)"
                  :key="role">
              {{ role }}
            </v-chip>
            </div>
            <div class="mt-12">
              <span class="form-field-label">Joined:</span> {{ formatDateAndTime(user.created_at) }}
            </div>
            <div class="mt-4">
              <div v-if="userStatus !== 'DEACTIVATED'">
                <span class="form-field-label pr-2">Account is:</span>
                <v-chip label color="green">Active</v-chip>
              </div>
              <div v-else>
                <span class="form-field-label pr-2">Account is:</span>
                <v-chip label color="red darken-3">Deactivated</v-chip>
                <div style="font-style:italic;font-size:0.92rem;" class="form-field-label pr-2 mt-3">Deactivated: {{ formatDateAndTime(user.deactivated_at) }}</div>
              </div>
            </div>
          </v-col>

          <!--------------------->
          <!-- Role management -->
          <!--------------------->
          <v-col cols="12" xs="12" sm="5" md="4" lg="4" class="pa-0 ma-0 pb-8">
            <div>
              <span style="font-style:italic;" class="form-field-label">Manage Roles</span>
            </div>

            <!-- (Admin) -->
            <div class="mt-4">
              <dialog-confirm-button
                  v-if="hasRole('admin')"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :buttonOutlined="true"
                  :disabled="!canManageRoles || isOwner"
                  buttonText="Revoke Admin"
                  buttonColor="amber darken-3"
                  message="Are you sure you want to revoke the Admin role?"
                  v-on:confirmed="revokeRole('admin')" />
              <dialog-confirm-button
                  v-else
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles"
                  buttonText="Grant Admin"
                  buttonColor="amber darken-3"
                  message="Are you sure you want to grant Admin to this user?"
                  v-on:confirmed="grantRole('admin')" />
            </div>

            <!-- (Dispatcher - Shipper Only) -->
            <div class="mt-4" v-if="uiType === 'shipper'">
              <dialog-confirm-button
                  v-if="hasRole('dispatcher')"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :buttonOutlined="true"
                  :disabled="!canManageRoles || hasRole('admin')"
                  buttonText="Revoke Dispatcher"
                  buttonColor="orange darken-4"
                  message="Are you sure you want to revoke the Dispatcher role?"
                  v-on:confirmed="revokeRole('dispatcher')" />
              <dialog-confirm-button
                  v-else
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles || hasRole('admin')"
                  buttonText="Grant Dispatcher"
                  buttonColor="orange darken-4"
                  message="Are you sure you want to grant Dispatcher to this user?"
                  v-on:confirmed="grantRole('dispatcher')" />
            </div>

            <!-- (Driver - Shipper Only) -->
            <div class="mt-4" v-if="uiType === 'shipper'">
              <dialog-confirm-button
                  v-if="hasRole('driver')"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :buttonOutlined="true"
                  :disabled="!canManageRoles"
                  buttonText="Revoke Driver"
                  buttonColor="blue-grey lighten-1"
                  message="Are you sure you want to revoke the Driver role?"
                  v-on:confirmed="revokeRole('driver')" />
              <dialog-confirm-button
                  v-else
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles"
                  buttonText="Grant Driver"
                  buttonColor="blue-grey lighten-1"
                  message="Are you sure you want to grant Driver to this user?"
                  v-on:confirmed="grantRole('driver')" />
            </div>

            <!-- I suppose there is no reason to grant a sub role here, you are just not an admin -->
            <!-- (Member - Receiver Only) -->
            <!-- <div class="mt-4" v-if="uiType === 'receiver'">
              <dialog-confirm-button
                  v-if="hasRole('member')"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :buttonOutlined="true"
                  :disabled="!canManageRoles"
                  buttonText="Revoke Member"
                  buttonColor="blue-grey lighten-1"
                  message="Are you sure you want to revoke the Member role?"
                  v-on:confirmed="revokeRole('member')" />
              <dialog-confirm-button
                  v-else
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles"
                  buttonText="Grant Member"
                  buttonColor="blue-grey lighten-1"
                  message="Are you sure you want to grant Member to this user?"
                  v-on:confirmed="grantRole('member')" />
            </div> -->
          </v-col>

          <!------------------------>
          <!-- Account management -->
          <!------------------------>
          <v-col cols="12" xs="12" sm="8" md="3" lg="4" class="pa-0 ma-0">
            <div>
              <span style="font-style:italic;" class="form-field-label">Manage Account</span>
            </div>
            <div class="mt-4">
              <dialog-confirm-button
                  v-if="userStatus !== 'DEACTIVATED'"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles || isOwner"
                  buttonText="Deactivate Account"
                  buttonColor="red darken-3"
                  message="Are you sure you want to deactivate this user?"
                  v-on:confirmed="deactivateUser" />
              <dialog-confirm-button
                  v-else-if="userStatus === 'DEACTIVATED'"
                  :buttonWidth="200"
                  :buttonHeight="43"
                  :disabled="!canManageRoles"
                  buttonText="Activate Account"
                  buttonColor="green"
                  message="Are you sure you want to activate this user?"
                  v-on:confirmed="activateUser" />
            </div>
          </v-col>
        </v-row>
      </v-container>

    </v-container> <!-- END - page-content -->

  </v-container>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'UserItemPage',

  components: {
    DialogConfirmButton
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null
    }
  },

  props: {
    userId: {
      type: String,
      default: null
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    isOwner () {
      return this.hasRole('owner')
    },
    canManageRoles () {
      return this.$root.activeUserHasRole('admin')
    },
    userTitleForDisplay () {
      return (this.user) ? `${this.user.first_name} ${this.user.last_name}` : ''
    },
    // The loaded user
    user () {
      return this.$parent.user
    },
    // The loaded user roles
    userRoles () {
      return this.user.roles || []
    },
    // The loaded user status
    userStatus () {
      return this.user.user_status
    }
  },

  async beforeDestroy () {
    this.dataLoadingError = null
  },

  methods: {
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MM/DD/YYYY') : ''
    },

    formatDateAndTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM DD YYYY, h:mma z') : ''
    },

    // Go back to list page
    close () {
      this.$root.goTo('user-list')
    },

    hasRole (role) {
      return this.$root.utils.includes(this.userRoles, role)
    },

    getRoleChipColor (role) {
      switch (role) {
        case 'owner': return 'blue'
        case 'admin': return 'amber darken-3'
        case 'dispatcher': return 'orange darken-4'
        case 'driver': return 'blue-grey lighten-1'
        case 'member': return 'blue-grey lighten-1'
        default: return 'grey'
      }
    },

    async grantRole (role) {
      if (!role) return false
      this.isDataLoading = true

      try {
        // Grant role
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `user/${this.userId}/add-role`,
          opts: {
            bodyData: {
              role
            }
          },
          to_model: false
        })

        // Reload user
        this.$parent.loadUser()
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    async revokeRole (role) {
      if (!role) return false
      this.isDataLoading = true

      try {
        // Revoke role
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `user/${this.userId}/remove-role`,
          opts: {
            bodyData: {
              role
            }
          },
          to_model: false
        })

        // Reload user
        this.$parent.loadUser()
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    async deactivateUser () {
      this.isDataLoading = true

      try {
        // Deactivate user account
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `user/${this.userId}/deactivate`
        })

        // Reload user
        this.$parent.loadUser()
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    async activateUser () {
      this.isDataLoading = true

      try {
        // Activate user account
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `user/${this.userId}/activate`
        })

        // Reload user
        this.$parent.loadUser()
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .user-item .form-field-label {
    margin-right: 4px;
  }

  .user-item .divider {
    border-bottom: 1px solid #32395F;
  }

  .user-item .content-title {
    font-size: 18px;
  }
</style>
